import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { BadgeCheck } from "lucide-react";

import { cn } from "@/utils/cn";

/**
 * Sizes from docs/ui/design-system.md §11 Avatar: XS (24px, inline chat/
 * lists) · SM (32px, list rows) · MD (40px, headers, the default) · LG
 * (64px, profile header) · XL (96px, own-profile edit view).
 */
const AVATAR_SIZE_CLASSES = {
  xs: "h-6 w-6",
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-16 w-16",
  xl: "h-24 w-24",
} as const;

export type AvatarSize = keyof typeof AVATAR_SIZE_CLASSES;

interface AvatarProps extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> {
  size?: AvatarSize;
  /** Online-status dot overlay (component-inventory.md Avatar variants). */
  showOnlineDot?: boolean;
  /** Verified-badge overlay — only render when the underlying verification
   * flag is actually true (design-system.md §11: "never a purely cosmetic
   * verified-looking treatment"). */
  showVerifiedBadge?: boolean;
}

const Avatar = React.forwardRef<React.ElementRef<typeof AvatarPrimitive.Root>, AvatarProps>(
  ({ className, size = "md", showOnlineDot, showVerifiedBadge, children, ...props }, ref) => (
    <span className="relative inline-flex shrink-0">
      <AvatarPrimitive.Root
        ref={ref}
        className={cn(
          "relative flex shrink-0 overflow-hidden rounded-full",
          AVATAR_SIZE_CLASSES[size],
          className,
        )}
        {...props}
      >
        {children}
      </AvatarPrimitive.Root>
      {showOnlineDot && (
        <span
          className="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full border-2 border-background bg-success"
          aria-hidden="true"
        />
      )}
      {showVerifiedBadge && (
        <span
          className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full border-2 border-background bg-info text-background"
          aria-hidden="true"
        >
          <BadgeCheck className="h-3 w-3" />
        </span>
      )}
    </span>
  ),
);
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn("aspect-square h-full w-full", className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted",
      className,
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };
